import React, { useEffect, useRef, useState } from 'react'

// Digit "wells", one per digit of however long the code currently is. The count
// comes from the caller rather than being fixed here, so the server can change
// the code length without a client release. The next-to-fill well shows a caret
// border. On shakeKey change (error), the row shakes and flashes red, then
// restores. On focusKey change (a refused code was cleared), focus goes back
// to the first box -- typing goes through the keypad's window listener either
// way, so this is where the eye and a screen reader land, not an input.
export const CodeWells = ({ digits, length = 4, shakeKey = 0, focusKey = 0 }) => {
  const [erroring, setErroring] = useState(false)
  const first = useRef(true)
  const firstFocus = useRef(true)
  const row = useRef(null)

  useEffect(() => {
    if (first.current) { first.current = false; return }
    setErroring(true)
    const t = setTimeout(() => setErroring(false), 400)
    return () => clearTimeout(t)
  }, [shakeKey])

  useEffect(() => {
    if (firstFocus.current) { firstFocus.current = false; return }
    const well = row.current && row.current.querySelector('.ld-well')
    if (well) well.focus({ preventScroll: true })
  }, [focusKey])

  return (
    <div ref={row} className={`ld-wells ${erroring ? 'is-error' : ''}`} key={erroring ? `s${shakeKey}` : 'idle'}>
      {Array.from({ length }).map((_, i) => {
        const filled = i < digits.length
        const active = i === digits.length && !erroring
        return (
          <div key={i} tabIndex={-1} className={`ld-well ${filled ? 'is-filled' : ''} ${active ? 'is-active' : ''}`}>
            {filled ? digits[i] : ''}
          </div>
        )
      })}
    </div>
  )
}

// The 70ms per-well reveal stagger lives in login-device.scss as
// `calc(var(--i) * 70ms)` — kept in one place (CSS) since nothing here needs
// the number.
const EXIT_MS = 120

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches

// Code DISPLAY — the "show this code" side (the device requesting login), as
// opposed to CodeWells above, which belongs to the confirming device and fills
// one keystroke at a time. This row is mounted at the expected length before
// any request goes out, so the dialog never changes height once a code
// arrives — it just stops breathing and reveals digits left to right.
//
// A refresh hands back a brand new code directly (callAuthDevice replaces the
// old `code` prop with the new one, never with an intermediate empty value),
// so the fade-out/breathe/reveal sequence on refresh is driven here rather
// than falling out of the prop change on its own — the same reason CodeWells
// above times its own shake-then-restore instead of leaving it to the caller.
//
// See docs/superpowers/specs/2026-09-26-device-code-wells-motion-design.md.
export const CodeDisplay = ({ code, length = 4 }) => {
  const reducedMotion = prefersReducedMotion()

  // 'waiting' (breathing, nothing to show yet) | 'exiting' (the previous code
  // fading out ahead of a refresh) | 'shown' (settled digits).
  const [phase, setPhase] = useState(code ? 'shown' : 'waiting')
  const [shown, setShown] = useState(code ? code.split('') : [])
  const prevCode = useRef(code)

  useEffect(() => {
    if (code === prevCode.current) return
    const had = prevCode.current
    prevCode.current = code

    if (!code) {
      // Request failed / was cleared — nothing to fade, just go back to waiting.
      setPhase('waiting')
      setShown([])
      return
    }

    if (!had || reducedMotion) {
      // Nothing was on screen yet, so there's nothing to fade before revealing —
      // or motion is off, so a refresh swaps straight to the new digits with no
      // fade/breathe interlude at all.
      setPhase('shown')
      setShown(code.split(''))
      return
    }

    // Refresh: fade the old code out together, breathe briefly, reveal the new one.
    setPhase('exiting')
    const t = setTimeout(() => {
      setPhase('waiting')
      setShown([])
      // One tick later so the waiting -> shown transition actually plays
      // instead of the new digits landing in the same paint as the old ones
      // leaving.
      requestAnimationFrame(() => {
        setPhase('shown')
        setShown(code.split(''))
      })
    }, EXIT_MS)
    return () => clearTimeout(t)
  }, [code, reducedMotion])

  const count = shown.length || length
  const fontSize = count <= 4 ? '2.5rem' : count <= 6 ? '2rem' : '1.5rem'
  const waiting = phase === 'waiting'
  // 'exiting' still shows the outgoing digits (CSS fades them out) — only
  // 'waiting' actually has nothing to show.
  const visible = phase === 'shown' || phase === 'exiting'

  return (
    <React.Fragment>
      <div
        className={`ld-wells ld-wells--display ${waiting ? 'is-waiting' : ''} ${phase === 'exiting' ? 'is-exiting' : ''} ${reducedMotion ? 'is-reduced-motion' : ''}`}
        aria-busy={waiting} data-testid="ld-wells"
      >
        {/* data-testid, not the class alone: the wells are aria-hidden (the
            live region below is what a screen reader gets), so byRole can't
            reach them and tests need a Testing-Library-approved way in. */}
        {Array.from({ length: count }).map((_, i) => (
          <div key={i} className={`ld-well is-display ${phase === 'shown' ? 'is-filled is-revealed' : ''}`}
            style={{ fontSize, '--i': i }} aria-hidden="true" data-testid="ld-well">
            <span className="ld-well__digit">{visible ? shown[i] : ''}</span>
          </div>
        ))}
      </div>
      {/* Announced once per code — there's no re-render trigger here between
          polls, so a screen reader isn't told the same digits every 5s. Spaced
          so they're read out one at a time rather than as a single number. */}
      <span className="visually-hidden" aria-live="polite">
        {phase === 'shown' && shown.length > 0 ? `Your code is ${shown.join(' ')}` : ''}
      </span>
    </React.Fragment>
  )
}
